import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";

import { AxiosResponse } from "axios";

export type CrudServiceProps<T,TId> = {
  id?: TId,
  defaultData?: T,
  getIdFromData?: (data:T) => TId,
  serializeId?: (id:TId) => string,
  onLoaded?: (data:T) => void,
  onDeleted?: (id:TId) => void,
  onSaved?: (data:T) => void,
  onError?: (error:any) => void,
  onIdChanged?: (id:TId) => void
};

export type CrudServiceType<T,TId> = (props: CrudServiceProps<T,TId>) => { 
  data: T|undefined,
  setData: React.Dispatch<React.SetStateAction<T|undefined>>,
  changeValues: (changedValues: any, allValues: any) => void,
  save: () => void,
  remove: () => void,
  getIdFromData: (data:T) => TId,
};


export const CrudApiService = function <T,TId>(props: 
  CrudServiceProps<T,TId> & {
    api: any,
    entityName: string,
  }
) {
 
  const { api, entityName } = props;
  const opSuffix = entityName.charAt(0).toUpperCase() + entityName.slice(1);

  const defaultData:T = props.defaultData || {} as T;
  const getIdFromData = props.getIdFromData || ((data:any) => { return data.id; });
  const serializeId = props.serializeId || ((id:TId) => { return id+''; });
  const getIdStrFromData = (data:any) => { return serializeId(getIdFromData(data)); };

  const [data, setData] = useState<T|undefined>(defaultData);
  const queryClient = useQueryClient()

  const save = () => { if (data) saveMutation.mutate(data) };
  const remove = () => {
    let id = data && getIdFromData(data);
    if (id) deleteMutation.mutate(id);
  };
  const changeValues = (changedValues:any, allValues:any) => { setData({...data, ...changedValues}); }

  const onLoaded = props.onLoaded || ((data:T) => {});
  const onSaved = props.onSaved || ((data:T) => {});
  const onDeleted = props.onDeleted || ((id:TId) => {});
  const onError = props.onError || ((error:any) => { 
    console.log(JSON.stringify(error, null, 4)); 
  });
  const onIdChanged = props.onIdChanged || ((id:TId) => {});

  // Esto genera 3 renderizados: 1 inicial, 2 loading, 3 success
  // @ts-ignore
  const id = props.id || (props.defaultData && getIdFromData(props.defaultData));
  useQuery([entityName, id], () => !id? ()=>{} : api['get'+opSuffix](id) , {
    onSuccess(response) {
      if (!response || !response.data) {
        if (!!id) onError(`Element ${id} doesn't exist`);
        return;
      }
      // onSuccess() // Remove errors
      setData(response.data);
      onLoaded(response.data)
    },
    onError: onError,
    retry: false,
    cacheTime: 0, // Seguro?
    refetchOnWindowFocus: false,
  });


  // @ts-ignore
  const saveMutation = useMutation<AxiosResponse<T>, unknown, T>(api['save'+opSuffix], {
    onSuccess: (response, variables, context) => {     
      // onSuccess() // Remove errors
      if (!data) return; // XXX cfillol: No debería de pasar
      let id = getIdFromData(data);
      if (id!==props.id) onIdChanged(id);
      setData(response.data);
      onSaved(response.data);
      queryClient.setQueryData([entityName, getIdStrFromData(response.data)], response);
    },
    onError: (error, variables, context) => {
      onError(error);
    }
  });

  // @ts-ignore
  const deleteMutation = useMutation<AxiosResponse<void>, unknown, TId>((id:TId) => api['delete'+opSuffix](id), {
    onSuccess: (response, id:TId, context) => {
      // onSuccess() // Remove errors
      onDeleted(id);
      queryClient.setQueryData([entityName, serializeId(id) ], {});
    },
    onError: onError
  });

  return { data, setData, changeValues, save, remove, getIdFromData };

};
